import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import type { LoginRequest, LoginResponse } from '@/types/auth.types';
import { CURRENT_USER_QUERY_KEY } from '@/hooks/useCurrentUser.hook';
import { ROUTES } from '@/routes/route.config';
import { authApi } from '@/services/api.service';
import { authStorage } from '@/services/authStorage.service';
import { loginRequestSchema } from '@/validators/auth.validator';

type FormState = LoginRequest;
type FieldErrors = Partial<Record<keyof FormState, string>>;

interface UseLoginPageResult {
  email: string;
  password: string;
  errors: FieldErrors;
  submitError: string | null;
  isSubmitting: boolean;
  onChangeEmail: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangePassword: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const INITIAL_FORM: FormState = { email: '', password: '' };

export const useLoginPage = (): UseLoginPageResult => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation<LoginResponse, unknown, LoginRequest>({
    mutationFn: (body) => authApi.login(body),
    onSuccess: (data) => {
      authStorage.setTokens({ token: data.token, refreshToken: data.refreshToken });
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);
      navigate(ROUTES.dashboard, { replace: true });
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const message =
          (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message ??
          'Unable to log in. Please try again.';
        setSubmitError(message);
        return;
      }
      setSubmitError('Unable to log in. Please try again.');
    },
  });

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>): void => {
    setForm((prev) => ({ ...prev, email: event.target.value }));
  };

  const onChangePassword = (event: ChangeEvent<HTMLInputElement>): void => {
    setForm((prev) => ({ ...prev, password: event.target.value }));
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setSubmitError(null);

    const parsed = loginRequestSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: fieldErrors.email?.[0],
        password: fieldErrors.password?.[0],
      });
      return;
    }

    setErrors({});
    mutation.mutate(parsed.data);
  };

  return {
    email: form.email,
    password: form.password,
    errors,
    submitError,
    isSubmitting: mutation.isPending,
    onChangeEmail,
    onChangePassword,
    onSubmit,
  };
};
